import Phaser from 'phaser';
import { Card, sortHand } from '../utils/CardUtils';
import { CardSprite } from './CardSprite';
import { CARD_WIDTH, CARD_HEIGHT, GAME_WIDTH, COLORS } from '../utils/Constants';

const ARC_SPREAD = 2.5; // degrees per card from center
const Y_DROP = 2.5; // pixels per unit from center (parabolic)
const OVERLAP_SPACING = 24; // pixels between card left edges
const HIGHLIGHT_DURATION = 2000;

export class HandDisplay {
  private scene: Phaser.Scene;
  private cardSprites: CardSprite[] = [];
  private cardBaseY: number[] = [];
  private cardBaseDepth: number[] = [];
  private x: number;
  private y: number;
  private faceUp: boolean;
  private maxWidth: number;
  private onCardClick?: (card: Card) => void;
  private onReorder?: (newOrder: Card[]) => void;
  private onDragToDiscard?: (card: Card) => void;
  private onDragToMeld?: (card: Card, meldId: string) => void;
  private dropZoneCheckers: {
    isOverDiscard?: (x: number, y: number) => boolean;
    getMeldAt?: (x: number, y: number) => string | null;
  } = {};
  private cards: Card[] = [];
  private interactive = true;

  // Pick-up reorder state
  private pickedUpIndex = -1;
  private pickedUpSprite: CardSprite | null = null;
  private previewInsertIndex = -1;
  private pointerMoveHandler?: (pointer: Phaser.Input.Pointer) => void;
  private pointerDownHandler?: (pointer: Phaser.Input.Pointer) => void;

  // Sort button
  private sortButton: Phaser.GameObjects.Container | null = null;

  // Highlight
  private highlightTimers: Phaser.Time.TimerEvent[] = [];
  private selectedCardIds: string[] = [];
  private drawnCardIds: string[] = [];

  constructor(scene: Phaser.Scene, x: number, y: number, faceUp: boolean, maxWidth = GAME_WIDTH - 200) {
    this.scene = scene;
    this.x = x;
    this.y = y;
    this.faceUp = faceUp;
    this.maxWidth = maxWidth;
  }

  setOnCardClick(callback: (card: Card) => void): void {
    this.onCardClick = callback;
  }

  setOnReorder(callback: (newOrder: Card[]) => void): void {
    this.onReorder = callback;
  }

  setOnDragToDiscard(callback: (card: Card) => void): void {
    this.onDragToDiscard = callback;
  }

  setOnDragToMeld(callback: (card: Card, meldId: string) => void): void {
    this.onDragToMeld = callback;
  }

  setDropZoneCheckers(checkers: {
    isOverDiscard?: (x: number, y: number) => boolean;
    getMeldAt?: (x: number, y: number) => string | null;
  }): void {
    this.dropZoneCheckers = checkers;
  }

  createSortButton(): void {
    if (!this.faceUp) return;

    this.sortButton = this.scene.add.container(0, 0).setDepth(250);

    const bg = this.scene.add.rectangle(0, 0, 60, 30, COLORS.BUTTON, 0.9)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => bg.setFillStyle(COLORS.BUTTON_HOVER, 1))
      .on('pointerout', () => bg.setFillStyle(COLORS.BUTTON, 0.9))
      .on('pointerdown', () => {
        this.cards = sortHand(this.cards);
        if (this.onReorder) this.onReorder(this.cards);
        this.renderCards();
      });

    const text = this.scene.add.text(0, 0, 'Sort', {
      fontSize: '13px',
      color: COLORS.TEXT_LIGHT,
      fontFamily: 'Georgia, serif',
    }).setOrigin(0.5);

    this.sortButton.add([bg, text]);
  }

  private updateSortButtonPosition(): void {
    if (!this.sortButton) return;
    const totalCards = this.cards.length || 1;
    const spacing = Math.min(OVERLAP_SPACING, this.maxWidth / totalCards);
    const totalWidth = spacing * (totalCards - 1) + CARD_WIDTH;
    const rightEdge = this.x + totalWidth / 2;
    this.sortButton.setPosition(rightEdge + 40, this.y);
  }

  updateCards(cards: Card[], interactive = true, highlightIds?: string[]): void {
    this.cards = [...cards];
    this.interactive = interactive;

    // Set new drawn highlights, or preserve existing ones
    if (highlightIds && highlightIds.length > 0) {
      this.drawnCardIds = [...highlightIds];
    }

    this.renderCards();
    this.applyVisualStates();
  }

  clearDrawHighlights(): void {
    this.drawnCardIds = [];
    this.applyVisualStates();
  }

  private renderCards(): void {
    // Remove old sprites
    for (const sprite of this.cardSprites) {
      sprite.destroy();
    }
    this.cardSprites = [];
    this.cardBaseY = [];
    this.cardBaseDepth = [];

    if (this.cards.length === 0) return;

    const totalCards = this.cards.length;
    const center = (totalCards - 1) / 2;

    // Calculate spacing - use overlap spacing but cap by maxWidth
    const spacing = Math.min(OVERLAP_SPACING, this.maxWidth / totalCards);
    const totalWidth = spacing * (totalCards - 1) + CARD_WIDTH;
    const startX = this.x - totalWidth / 2 + CARD_WIDTH / 2;

    // Face-up hands arc downward (+1), face-down hands arc upward (-1)
    const arcDir = this.faceUp ? 1 : -1;

    for (let i = 0; i < this.cards.length; i++) {
      const cardX = startX + i * spacing;

      // Arc: rotation and y-offset
      const distFromCenter = i - center;
      const angle = distFromCenter * ARC_SPREAD * arcDir;
      const yOffset = Math.abs(distFromCenter) * Y_DROP;

      const cardY = this.y + yOffset * arcDir;
      const cardDepth = 10 + i;
      const sprite = new CardSprite(this.scene, cardX, cardY, this.cards[i], this.faceUp);
      sprite.setDepth(cardDepth);
      sprite.setAngle(angle);

      if (this.faceUp && this.interactive) {
        this.setupCardInteraction(sprite, i);
      }

      this.cardSprites.push(sprite);
      this.cardBaseY.push(cardY);
      this.cardBaseDepth.push(cardDepth);
    }

    this.updateSortButtonPosition();
  }

  private setupCardInteraction(sprite: CardSprite, index: number): void {
    sprite.enableHover();

    sprite.on('pointerdown', (_pointer: Phaser.Input.Pointer) => {
      // If we already have a card picked up, this click places it
      if (this.pickedUpIndex >= 0) {
        return; // handled by the scene-level pointerdown
      }

      // Regular click — fire card click callback (for selection)
      if (this.onCardClick) {
        this.onCardClick(sprite.card);
      }
    });
  }

  /** Pick up a card by index for reordering. Call from outside or bind to long-press/double-click. */
  pickUpCard(index: number): void {
    if (this.pickedUpIndex >= 0) return; // already holding one
    if (index < 0 || index >= this.cards.length) return;

    this.pickedUpIndex = index;
    const sprite = this.cardSprites[index];
    if (!sprite) return;

    // Create a floating copy that follows the pointer
    this.pickedUpSprite = new CardSprite(this.scene, sprite.x, sprite.y, sprite.card, this.faceUp);
    this.pickedUpSprite.setDepth(500);
    this.pickedUpSprite.setAlpha(0.85);
    this.pickedUpSprite.setAngle(0);
    this.pickedUpSprite.disableInteractive();

    // Hide the original card in the layout
    sprite.setVisible(false);

    // Preview starts at current position
    this.previewInsertIndex = index;

    // Listen for pointer move on the scene to update preview position
    this.pointerMoveHandler = (pointer: Phaser.Input.Pointer) => {
      if (!this.pickedUpSprite) return;
      this.pickedUpSprite.setPosition(pointer.x, pointer.y);

      // Calculate which slot the card would go into
      const insertIdx = this.getInsertionIndex(pointer.x);
      if (insertIdx !== this.previewInsertIndex) {
        this.previewInsertIndex = insertIdx;
        this.renderCardsWithGap(this.pickedUpIndex, insertIdx);
      }
    };

    this.pointerDownHandler = (pointer: Phaser.Input.Pointer) => {
      this.placePickedUpCard(pointer);
    };

    this.scene.input.on('pointermove', this.pointerMoveHandler);

    // Defer pointerdown listener to next frame so the current click doesn't immediately place the card
    this.scene.time.delayedCall(0, () => {
      if (this.pickedUpIndex >= 0 && this.pointerDownHandler) {
        this.scene.input.on('pointerdown', this.pointerDownHandler);
      }
    });

    // Show the gap in layout immediately
    this.renderCardsWithGap(index, index);
  }

  private placePickedUpCard(pointer: Phaser.Input.Pointer): void {
    if (this.pickedUpIndex < 0) return;

    // Check if dropped on discard pile
    if (this.dropZoneCheckers.isOverDiscard && this.dropZoneCheckers.isOverDiscard(pointer.x, pointer.y)) {
      const card = this.cards[this.pickedUpIndex];
      this.cancelPickUp();
      if (this.onDragToDiscard) {
        this.onDragToDiscard(card);
      }
      return;
    }

    // Check if dropped on a meld
    if (this.dropZoneCheckers.getMeldAt) {
      const meldId = this.dropZoneCheckers.getMeldAt(pointer.x, pointer.y);
      if (meldId) {
        const card = this.cards[this.pickedUpIndex];
        this.cancelPickUp();
        if (this.onDragToMeld) {
          this.onDragToMeld(card, meldId);
        }
        return;
      }
    }

    // Reorder within hand
    const newIndex = this.getInsertionIndex(pointer.x);
    const oldIndex = this.pickedUpIndex;

    // Clean up pick-up state
    this.cleanUpPickUp();

    if (newIndex !== oldIndex) {
      const [removed] = this.cards.splice(oldIndex, 1);
      const insertAt = newIndex > oldIndex ? newIndex - 1 : newIndex;
      this.cards.splice(insertAt, 0, removed);
      if (this.onReorder) this.onReorder(this.cards);
    }
    this.renderCards();
    this.applyVisualStates();
  }

  cancelPickUp(): void {
    this.cleanUpPickUp();
    this.renderCards();
    this.applyVisualStates();
  }

  private cleanUpPickUp(): void {
    if (this.pickedUpSprite) {
      this.pickedUpSprite.destroy();
      this.pickedUpSprite = null;
    }
    if (this.pointerMoveHandler) {
      this.scene.input.off('pointermove', this.pointerMoveHandler);
      this.pointerMoveHandler = undefined;
    }
    if (this.pointerDownHandler) {
      this.scene.input.off('pointerdown', this.pointerDownHandler);
      this.pointerDownHandler = undefined;
    }
    this.pickedUpIndex = -1;
    this.previewInsertIndex = -1;
  }

  isHoldingCard(): boolean {
    return this.pickedUpIndex >= 0;
  }

  private getInsertionIndex(pointerX: number): number {
    if (this.cards.length === 0) return 0;

    // Calculate card positions (excluding the picked-up card) to find insertion point
    const totalCards = this.cards.length;
    const spacing = Math.min(OVERLAP_SPACING, this.maxWidth / totalCards);
    const totalWidth = spacing * (totalCards - 1) + CARD_WIDTH;
    const startX = this.x - totalWidth / 2 + CARD_WIDTH / 2;

    for (let i = 0; i < totalCards; i++) {
      if (i === this.pickedUpIndex) continue;
      const cardX = startX + i * spacing;
      if (pointerX < cardX) return i;
    }
    return totalCards;
  }

  /** Render cards with a visual gap at insertIndex, hiding the card at skipIndex */
  private renderCardsWithGap(skipIndex: number, insertIndex: number): void {
    const totalCards = this.cards.length;
    const visibleCount = totalCards - 1; // one card is picked up
    const center = visibleCount / 2;
    const spacing = Math.min(OVERLAP_SPACING, this.maxWidth / (visibleCount + 1)); // +1 for the gap
    const totalWidth = spacing * visibleCount + CARD_WIDTH;
    const startX = this.x - totalWidth / 2 + CARD_WIDTH / 2;

    const arcDir = this.faceUp ? 1 : -1;
    let slot = 0;

    for (let i = 0; i < totalCards; i++) {
      const sprite = this.cardSprites[i];
      if (!sprite) continue;

      if (i === skipIndex) {
        sprite.setVisible(false);
        continue;
      }

      // Leave a gap at the insert position
      const adjustedInsert = insertIndex > skipIndex ? insertIndex - 1 : insertIndex;
      if (slot === adjustedInsert) slot++; // skip a slot for the gap

      const cardX = startX + slot * spacing;
      const distFromCenter = slot - center;
      const angle = distFromCenter * ARC_SPREAD * arcDir;
      const yOffset = Math.abs(distFromCenter) * Y_DROP;
      const cardY = this.y + yOffset * arcDir;

      sprite.setVisible(true);
      sprite.setPosition(cardX, cardY);
      sprite.setAngle(angle);
      sprite.setDepth(10 + slot);

      slot++;
    }
  }

  private applyDrawHighlight(cardIds: string[]): void {
    // Store drawn card IDs — glow persists until cleared
    this.drawnCardIds = [...cardIds];
    this.applyVisualStates();
  }

  highlightCards(cardIds: string[]): void {
    this.selectedCardIds = [...cardIds];
    this.applyVisualStates();
  }

  clearHighlights(): void {
    this.selectedCardIds = [];
    this.applyVisualStates();
  }

  /** Apply all visual states: draw glow, selection raise + tint */
  private applyVisualStates(): void {
    for (let i = 0; i < this.cardSprites.length; i++) {
      const sprite = this.cardSprites[i];
      const baseY = this.cardBaseY[i];
      const baseDepth = this.cardBaseDepth[i];
      const isSelected = this.selectedCardIds.includes(sprite.card.id);
      const isDrawn = this.drawnCardIds.includes(sprite.card.id);

      // Reset to base position first
      sprite.selected = false;
      sprite.setY(baseY);
      sprite.setDepth(baseDepth);
      sprite.clearTint();

      if (isSelected) {
        // Mark as selected so hover doesn't reset it
        sprite.selected = true;
        sprite.setTint(0x88ff88);
        sprite.setY(baseY - 15);
        sprite.setDepth(baseDepth + 100);
      } else if (isDrawn) {
        // Persistent golden glow for newly drawn cards
        sprite.setTint(0xffff66);
      }
    }
  }

  getCardSprite(cardId: string): CardSprite | undefined {
    return this.cardSprites.find(s => s.card.id === cardId);
  }

  /** Get the target position for a card at a given index (for animation purposes) */
  getCardPosition(index: number, totalCards: number): { x: number; y: number } {
    const arcDir = this.faceUp ? 1 : -1;
    const center = (totalCards - 1) / 2;
    const spacing = Math.min(OVERLAP_SPACING, this.maxWidth / totalCards);
    const totalWidth = spacing * (totalCards - 1) + CARD_WIDTH;
    const startX = this.x - totalWidth / 2 + CARD_WIDTH / 2;
    const cardX = startX + index * spacing;
    const distFromCenter = index - center;
    const yOffset = Math.abs(distFromCenter) * Y_DROP;
    return { x: cardX, y: this.y + yOffset * arcDir };
  }

  /** Get the center position of this hand display */
  getCenterPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  destroy(): void {
    this.cleanUpPickUp();
    for (const sprite of this.cardSprites) {
      sprite.destroy();
    }
    this.cardSprites = [];
    for (const timer of this.highlightTimers) timer.destroy();
    this.highlightTimers = [];
    if (this.sortButton) {
      this.sortButton.destroy();
      this.sortButton = null;
    }
  }
}
